/**
 * Sign-off screen, presentational half: the deliverable summary and the
 * workflow-status row.
 *
 * Split out of `ValuationSignOff.tsx` under docs/FILE_SIZE_POLICY.md, on the
 * same seam its siblings use (`_SignOffGates`, `_SignOffReview`,
 * `_SnapshotHistory`): the screen orchestrates — loads, debounces, finalizes —
 * and these render. Nothing here fetches and nothing here decides.
 *
 * The distinction the status card exists to make visible, restated because it
 * is the one a reader gets wrong: **status is a workflow marker, the snapshot
 * is the attestation.** Dragging this row back to "In progress" does not unsign
 * anything, and it cannot — the signed record is append-only server-side.
 */
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import type { ValuationEngagement } from '@/types/valuation'
import {
  Fact,
  VALUATION_STATUSES,
  valueKindLabel,
  type ValuationStatus,
} from './_shared'
import { money } from '@/utils/format'
import { CollapsibleTitle, useCollapsible } from './_CollapsibleCard'

/** WALKFIX-B F12 (WF-B-2): the owner's estimate is never signed, so its method
 *  line carries no "(signed)" and no "SDE". Only the SDE basis has approved
 *  owner words; any other stored method falls back to its label. */
function ownerMethod(code: string | null | undefined): string | null {
  const m = code ? /^sde x(\d+\.\d{2})/.exec(code) : null
  return m ? `business earnings × ${m[1]}` : null
}

export function DeliverableCard({
  engagement,
  low,
  high,
  hasBand,
  positioning,
  previewLow,
  previewHigh,
  owner = false,
}: {
  engagement: ValuationEngagement
  low: number | null
  high: number | null
  hasBand: boolean
  positioning?: string | null
  /** The in-session engine band — shown only while nothing is persisted yet. */
  previewLow?: number | null
  previewHigh?: number | null
  /** valuation_owner: an unsigned estimate, in plain words (WALKFIX-B F12). */
  owner?: boolean
}) {
  const concluded = engagement.value_kind === 'concluded'
  return (
    <Card>
      <CardHeader><CardTitle>Deliverable</CardTitle></CardHeader>
      <CardContent>
        <Fact label="Engagement type" value={valueKindLabel(engagement.value_kind)} />
        <Fact
          label="Value"
          value={
            hasBand
              ? `${money(low)} – ${money(high)}`
              : previewLow != null && previewHigh != null
                ? `${money(previewLow)} – ${money(previewHigh)} (preview — not signed yet)`
                : 'Not calculated yet'
          }
        />
        <Fact label="Method" value={(owner && ownerMethod(engagement.valuation_method)) || (engagement.valuation_method_label ?? engagement.valuation_method ?? '—')} />
        <Fact label="Industry" value={engagement.industry_label ?? engagement.industry ?? '—'} />
        <Fact label="Period" value={engagement.period_label ?? '—'} />
        <p className="mt-3 text-sm text-pal-ink-body">
          {positioning ?? (concluded
            ? 'Conclusion of value (AICPA SSVS No. 1 valuation engagement).'
            // VAL-WORDING-APPLY-1 H8 (owner 10-29): the owner's estimate in plain words.
            : owner
              ? 'Your estimated value, calculated by the platform from your figures. It is not a signed valuation.'
              : 'Calculated value (AICPA SSVS No. 1 calculation engagement) — NOT a conclusion of value.')}
        </p>
      </CardContent>
    </Card>
  )
}

export function StatusCard({
  engagement,
  canConclude,
  saving,
  onSetStatus,
  collapsible = false,
}: {
  collapsible?: boolean
  engagement: ValuationEngagement
  canConclude: boolean
  saving: boolean
  onSetStatus: (next: ValuationStatus) => void
}) {
  const concluded = engagement.value_kind === 'concluded'
  const signedOff = engagement.status === 'signed_off'
  const c = useCollapsible(collapsible)
  return (
    <Card {...c.cardProps}>
      <CardHeader>
        <CollapsibleTitle collapsible={collapsible} open={c.open} titleId={c.titleId} onToggle={c.toggle}>Status</CollapsibleTitle>
      </CardHeader>
      {c.open && <CardContent className="space-y-4">
        <p className="text-xs text-pal-ink-muted">
          Status is a workflow marker on the engagement — it records where the work stands.
          The attestation is the signed snapshot above, not this row: Finalize sets this to
          “Signed off” as a consequence, and moving it back here does not unsign anything or
          alter the frozen record.
        </p>
        <div className="flex flex-wrap gap-2">
          {(Object.keys(VALUATION_STATUSES) as ValuationStatus[]).map(key => {
            const active = engagement.status === key
            const restricted = key === 'signed_off' && !canConclude
            return (
              <Button
                key={key}
                type="button"
                variant={active ? 'default' : 'outline'}
                size="sm"
                disabled={saving || active || restricted}
                onClick={() => onSetStatus(key)}
              >
                {VALUATION_STATUSES[key]}
              </Button>
            )
          })}
        </div>
        {!canConclude && (
          <p className="text-xs text-pal-warning-text">
            Your role cannot mark an engagement signed off.
          </p>
        )}
        {signedOff && (
          <p className="text-xs text-pal-success-text">
            Marked signed off. The deliverable remains a {concluded ? 'conclusion' : 'calculated'} value
            as fixed at creation.
          </p>
        )}
      </CardContent>}
    </Card>
  )
}
